import React, { useEffect, useState } from "react";
import { X, MapPin, Calendar, Clock, Check, AlertCircle, MessageCircle, CreditCard, Image as ImageIcon } from "lucide-react";
import { buildWaLink } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import BookingFlow from "./BookingFlow";

const formatIDR = (n) => `IDR ${Number(n).toLocaleString("id-ID")}`;

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`relative whitespace-nowrap px-1 pb-3 text-sm font-semibold transition-colors ${
      active ? "text-[#0C2230]" : "text-[#0C2230]/50 hover:text-[#0C2230]/80"
    }`}
  >
    {children}
    {active && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#0E9A57]" />}
  </button>
);

const Section = ({ title, children, testid }) => (
  <section data-testid={testid} className="mb-8">
    <h4 className="mb-4 font-[Fraunces] text-2xl font-medium text-[#0C2230]">{title}</h4>
    {children}
  </section>
);

export const PackageDetailModal = ({ pkg, onClose }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("overview");
  const [activeImg, setActiveImg] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const d = t.paket.detail;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!pkg) return null;

  const fullTitle = `${pkg.title}${pkg.subtitle ? ` – ${pkg.subtitle}` : ""}`;
  const waBook = buildWaLink(t.waMessages.book(fullTitle));
  const waAsk = buildWaLink(t.waMessages.ask(fullTitle));
  const savings = pkg.price_original && pkg.price_original > pkg.price_from ? pkg.price_original - pkg.price_from : 0;
  const gallery = (pkg.gallery?.length ? pkg.gallery : [pkg.image_url]).filter(Boolean);

  return (
    <div
      data-testid="package-detail-modal"
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm md:items-center"
      onClick={onClose}
    >
      <div
        className="relative flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:h-[88vh] md:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          data-testid="package-detail-close"
          onClick={onClose}
          aria-label={d.close}
          className="absolute right-5 top-5 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#0C2230] shadow-lg ring-1 ring-black/5 transition-colors hover:bg-white"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Hero image */}
        <div className="relative h-56 shrink-0 overflow-hidden md:h-72">
          <img src={gallery[activeImg]} alt={fullTitle} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {pkg.discount_percent ? (
            <span className="absolute left-5 top-5 z-10 rounded-md bg-gradient-to-r from-[#0E9A57] to-[#13b86a] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow">
              {pkg.discount_percent}% OFF
            </span>
          ) : null}
          <div className="absolute bottom-5 left-5 right-20 z-10 text-white">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 backdrop-blur">
                <MapPin className="h-3.5 w-3.5" /> Batam, Indonesia
              </span>
              {pkg.departure_from && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBBF24] px-3 py-1 font-semibold text-[#0C2230]">
                  {d.departFrom}: {pkg.departure_from}
                </span>
              )}
            </div>
            <h3 className="font-[Fraunces] text-2xl font-medium leading-tight md:text-3xl">{fullTitle}</h3>
          </div>
        </div>

        {/* Quick meta strip */}
        <div className="grid shrink-0 grid-cols-3 divide-x divide-[#0C2230]/8 border-b border-[#0C2230]/8 bg-[#FBFAF6]">
          <div className="flex items-center gap-2 px-4 py-3 md:px-6">
            <Clock className="h-4 w-4 text-[#1E7FC2]" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#0C2230]/55">{d.duration}</p>
              <p className="text-sm font-semibold text-[#0C2230]">{pkg.duration}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 md:px-6">
            <Calendar className="h-4 w-4 text-[#1E7FC2]" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#0C2230]/55">{d.itinerary}</p>
              <p className="text-sm font-semibold text-[#0C2230]">{pkg.itinerary?.length || 0} {d.day}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 md:px-6">
            <Check className="h-4 w-4 text-[#0E9A57]" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#0C2230]/55">{d.whatYouGet}</p>
              <p className="text-sm font-semibold text-[#0C2230]">{pkg.inclusions?.length || 0} items</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 border-b border-[#0C2230]/8 bg-white px-6 md:px-10">
          <div className="flex items-center gap-7 overflow-x-auto pt-4">
            <TabButton active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview">{d.overview}</TabButton>
            <TabButton active={tab === "itinerary"} onClick={() => setTab("itinerary")} testid="tab-itinerary">{d.itinerary}</TabButton>
            <TabButton active={tab === "inclusions"} onClick={() => setTab("inclusions")} testid="tab-inclusions">{d.inclusions}</TabButton>
            <TabButton active={tab === "exclusions"} onClick={() => setTab("exclusions")} testid="tab-exclusions">{d.exclusions}</TabButton>
            {gallery.length > 1 && (
              <TabButton active={tab === "gallery"} onClick={() => setTab("gallery")} testid="tab-gallery">{d.gallery}</TabButton>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-7 md:px-10 md:py-9">
          {tab === "overview" && (
            <>
              <Section title={d.overview} testid="section-overview">
                <p className="text-base leading-relaxed text-[#0C2230]/75">
                  {pkg.description || `${fullTitle} — ${pkg.duration} tour in Batam.`}
                </p>
              </Section>
              <Section title={d.whatYouGet} testid="section-highlights">
                <ul className="grid gap-2.5 md:grid-cols-2">
                  {(pkg.inclusions || []).slice(0, 6).map((inc, i) => (
                    <li key={`${inc}-${i}`} className="flex items-start gap-2.5">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0E9A57]/12 text-[#0E9A57]">
                        <Check className="h-3 w-3" />
                      </span>
                      <span className="text-sm leading-relaxed text-[#0C2230]/80">{inc}</span>
                    </li>
                  ))}
                </ul>
              </Section>
              <p className="rounded-xl bg-[#FBFAF6] p-4 text-xs leading-relaxed text-[#0C2230]/60">
                <AlertCircle className="mr-1.5 inline h-3.5 w-3.5 align-text-bottom text-[#B7892F]" />
                {d.notes}
              </p>
            </>
          )}

          {tab === "itinerary" && (
            <Section title={d.itinerary} testid="section-itinerary">
              <div className="space-y-6">
                {(pkg.itinerary || []).map((day) => (
                  <div key={`day-${day.day}`} data-testid={`itinerary-day-${day.day}`} className="relative pl-10">
                    <span className="absolute left-0 top-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#0E9A57] to-[#1E7FC2] text-xs font-bold text-white shadow">
                      {day.day}
                    </span>
                    <h5 className="mb-3 font-[Fraunces] text-xl font-medium text-[#0C2230]">
                      {d.day} {day.day} · {day.title}
                    </h5>
                    <ul className="space-y-2 border-l-2 border-dashed border-[#0C2230]/15 pl-5">
                      {(day.activities || []).map((act, i) => (
                        <li key={`d${day.day}-${i}`} className="text-sm leading-relaxed text-[#0C2230]/75">
                          {act}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {(!pkg.itinerary || pkg.itinerary.length === 0) && (
                  <p className="text-sm text-[#0C2230]/55">No detailed itinerary yet. Contact us via WhatsApp.</p>
                )}
              </div>
            </Section>
          )}

          {tab === "inclusions" && (
            <Section title={d.inclusions} testid="section-inclusions">
              <ul className="space-y-3">
                {(pkg.inclusions || []).map((inc, i) => (
                  <li key={`inc-${inc}-${i}`} className="flex items-start gap-3 rounded-lg bg-[#FBFAF6] p-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0E9A57] text-white">
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="text-sm leading-relaxed text-[#0C2230]/85">{inc}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {tab === "exclusions" && (
            <Section title={d.exclusions} testid="section-exclusions">
              <ul className="space-y-3">
                {(pkg.exclusions || []).map((exc, i) => (
                  <li key={`exc-${exc}-${i}`} className="flex items-start gap-3 rounded-lg bg-[#FBF2F2] p-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#DC2626] text-white">
                      <X className="h-3 w-3" />
                    </span>
                    <span className="text-sm leading-relaxed text-[#0C2230]/85">{exc}</span>
                  </li>
                ))}
                {(!pkg.exclusions || pkg.exclusions.length === 0) && (
                  <p className="text-sm text-[#0C2230]/55">—</p>
                )}
              </ul>
            </Section>
          )}

          {tab === "gallery" && (
            <Section title={d.gallery} testid="section-gallery">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {gallery.map((url, i) => (
                  <button
                    key={url}
                    onClick={() => setActiveImg(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`relative aspect-[4/3] overflow-hidden rounded-xl ring-2 transition-all ${
                      activeImg === i ? "ring-[#0E9A57]" : "ring-transparent hover:ring-[#0E9A57]/30"
                    }`}
                  >
                    <img src={url} alt={`gallery-${i}`} className="absolute inset-0 h-full w-full object-cover" />
                  </button>
                ))}
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-[#0C2230]/55">
                <ImageIcon className="h-3.5 w-3.5" /> Click a thumbnail to set hero image
              </p>
            </Section>
          )}
        </div>

        {/* Sticky footer with price + CTAs */}
        <div className="shrink-0 border-t border-[#0C2230]/10 bg-white px-6 py-4 shadow-[0_-8px_24px_-12px_rgba(12,34,48,0.18)] md:px-10">
          <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              {pkg.price_original && pkg.price_original > pkg.price_from ? (
                <p className="text-xs text-[#DC2626] line-through">{formatIDR(pkg.price_original)}</p>
              ) : (
                <p className="text-xs text-[#0C2230]/55">{d.priceFrom}</p>
              )}
              <p className="font-[Fraunces] text-2xl font-bold text-[#0C2230] md:text-3xl">
                {formatIDR(pkg.price_from)}
                <span className="ml-1 text-sm font-normal text-[#0C2230]/55">{d.perPax}</span>
              </p>
              {savings > 0 && (
                <p className="text-xs font-semibold text-[#0E9A57]">{d.saveAmount} {formatIDR(savings)}</p>
              )}
            </div>
            <div className="flex gap-2.5">
              <a
                href={waAsk}
                target="_blank"
                rel="noreferrer"
                data-testid="detail-ask"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#0C2230]/15 bg-white px-5 py-3 text-sm font-semibold text-[#0C2230] transition-colors hover:bg-[#FBFAF6] md:flex-none"
              >
                <MessageCircle className="h-4 w-4" /> {d.askMore}
              </a>
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                data-testid="detail-book"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0E9A57] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#0c8a4d] hover:shadow-lg md:flex-none"
              >
                <Calendar className="h-4 w-4" /> {t.booking?.title || "Book Now"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {bookingOpen && (
        <BookingFlow pkg={pkg} onClose={() => setBookingOpen(false)} />
      )}
    </div>
  );
};

export default PackageDetailModal;
